/**
 * A special kind of object that behaves similar to a ref or computed property,
 * but stores values in local storage.
 *
 * Can be used in pinia stores or vue components.
 *
 * The difference to vue refs is that this value will be
 * persisted even when the user closes or refreshes the browser.
 *
 * Note that local storage is not encrypted, anyone with access
 * to the users browser can read or modify data stored here.
 *
 * Access is wrapped in try/catch: `window.localStorage` can throw in two
 * realistic conditions —
 *   - in production: when the browser disables storage (private mode,
 *     storage quota disabled, etc.);
 *   - in tests: when a leaked `setInterval` (e.g. from useIdleTimeout) fires
 *     after the spec file's jsdom is torn down and `window` is no longer
 *     reachable, producing an unhandled exception that fails the run.
 *
 * On failure the read returns the empty string and the write is silently
 * dropped. Callers MUST handle the empty-string read as "value unknown" and
 * fail closed per their security model — see `useIdleTimeout`'s
 * `recordTimeoutChange` for the H1SR-114 fail-closed pattern. Luxon does
 * NOT rescue this implicitly: `DateTime.fromISO('')` is invalid and the
 * downstream `Interval.length()` is `NaN`, which silently bypasses any
 * `value > threshold` comparison (`NaN > x` is always `false`).
 */
export const persisted = (key: string) => ({
    get value() {
        try {
            return window.localStorage.getItem(key) || '';
        } catch {
            return '';
        }
    },
    set value(newValue: string) {
        try {
            window.localStorage.setItem(key, newValue);
        } catch {
            // window.localStorage unavailable — see file header for cases.
        }
    },
});
